/**
 * "Has this been on screen yet?", answered once and then never revised.
 *
 * Once is the whole point. A figure that replays every time it scrolls past
 * turns the page into a carousel of things demanding to be watched again, and
 * the second viewing carries no information the first did not. So the observer
 * disconnects on the first intersection and the flag never goes back to false.
 *
 * Under reduced motion this returns `true` immediately and never observes
 * anything: the caller's job in that mode is to render the resting state, and
 * making it wait for a scroll to do that would hide content behind a gesture.
 */
import { useEffect, useRef, useState } from 'react'

import { useReducedMotionSafe } from './motion'

export function useInViewOnce<T extends Element = HTMLDivElement>(
  /** How far into the viewport the element must come. Negative insets the edge. */
  margin = '-15%',
): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null)
  const reduced = useReducedMotionSafe()
  const [seen, setSeen] = useState(false)

  useEffect(() => {
    if (reduced || seen) return
    const node = ref.current
    // No IntersectionObserver (jsdom, an old browser) must not mean "never
    // visible" — that would leave the figure at its entrance offset forever.
    if (!node || typeof IntersectionObserver !== 'function') {
      setSeen(true)
      return
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setSeen(true)
          observer.disconnect()
        }
      },
      { rootMargin: margin },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [margin, reduced, seen])

  return [ref, reduced ? true : seen]
}
